import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import axiosRaw from 'axios'; // Raw axios for Python backend calls
import axios from '../../../utils/axios';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  addToCart,
  resetCart,
  decreaseQuantity,
  deleteCart,
} from '../../../redux/slices/product';
import { useProductTranslation } from '../../../hooks/useProductTranslation';
import { useAuthContext } from '../../../auth/useAuthContext';

// Enhanced types
import {
  ExtractedItem,
  CompletedTrioItem,
  OrderState,
  OrderStep,
  TrioDetails,
  ExtractSingleTrioResponse,
  TrioSelectionsResponse
} from '../types/voiceOrderingTypes';

import {
  detectCommand,
  containsTrioKeyword,
  splitTranscriptAtTrio,
  isNumeric,
  generateInstructions,
  extractRemovalTarget,
} from '../utils/trioDetection';

interface UseVoiceOrderingProps {
  products: any[];
  cart: any[];
  isEnabled?: boolean;
}

export function useVoiceOrderingEnhanced({ products, cart, isEnabled = true }: UseVoiceOrderingProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuthContext();

  // Récupère la langue par défaut de l'organisation depuis Redux
  const organization = useSelector((state: RootState) => state.organization.organization);
  const orgDefaultLanguage = organization?.defaultLanguage;

  // Le hook de traduction utilise la langue de l'org comme priorité
  const { getName } = useProductTranslation(orgDefaultLanguage);

  // Enhanced order state
  const [orderState, setOrderState] = useState<OrderState>({
    soloItems: [],
    trioItems: [],
    phase: 'idle',
    trioSelection: null,
    accumulatedTranscript: '',
    pendingTranscript: '',
    steps: [],
    isProcessingOrder: false,
    isConfirmingOrder: false,
    loadingDots: 0,
    lastInteractedProductId: null,
  });

  const [aiRequestCount, setAiRequestCount] = useState(0);

  const [isWarningPhase, setIsWarningPhase] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());

  // Speech synthesis
  const [speech, setSpeech] = useState('Bonjour, je vous écoute.');

  // Refs
  const organizationIdRef = useRef('');
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartedAtRef = useRef<string | null>(null);
  const stepIndexRef = useRef<number>(0);
  const nextStepIsCorrectionRef = useRef<boolean>(false);
  const aiRequestCountRef = useRef<number>(0);
  // Prevent self-echo: track when TTS is speaking
  const ttsSpeakingRef = useRef<boolean>(false);
  const productsRef = useRef<any[]>(products);
  const cartRef = useRef<any[]>(cart);
  const lastProcessedTranscript = useRef<string>('');

  const ensureSessionStarted = useCallback(async () => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
      sessionStartedAtRef.current = new Date().toISOString();
      stepIndexRef.current = 0;
      nextStepIsCorrectionRef.current = false;
      aiRequestCountRef.current = 0;
      setAiRequestCount(0);
      
      try {
        const userResponse = await axios.get('/api/v1/users/my-account');
        const authUser: any = userResponse.data;
        const subOrgId = authUser.subOrgId;
        
        await axios.post('/api/v1/orders', {
          buyerId: 'ff808181977e4e6401977e51a9c70001',
          organizationId: subOrgId,
          sessionId: sessionIdRef.current,
          sessionStartedAt: sessionStartedAtRef.current,
          isAbandoned: false,
          humanTakeover: false,
          orderSource: 'AI_VOICE',
          items: [],
          steps: []
        });
        console.log('✅ Session enregistrée comme ACTIVE.');
      } catch (err) {
        console.error('⚠️ Erreur lors de la création de la session ACTIVE:', err);
      }
    }
  }, []);

  const closeCurrentSession = useCallback(() => {
    sessionIdRef.current = null;
    sessionStartedAtRef.current = null;
    stepIndexRef.current = 0;
    nextStepIsCorrectionRef.current = false;
    aiRequestCountRef.current = 0;
    setAiRequestCount(0);
  }, []);

  const incrementAiRequestCount = useCallback(async () => {
    await ensureSessionStarted();

    const sessionId = sessionIdRef.current;
    if (!sessionId) {
      return;
    }

    try {
      const response = await axios.patch(`/api/v1/orders/session/${sessionId}/ai-request`);
      const updatedCount = response.data?.aiRequestCount ?? (aiRequestCountRef.current + 1);
      aiRequestCountRef.current = updatedCount;
      setAiRequestCount(updatedCount);
    } catch (err) {
      console.error('⚠️ Impossible de synchroniser le compteur de requêtes IA:', err);
    }
  }, [ensureSessionStarted]);

  const appendStep = useCallback((step: Omit<OrderStep, 'stepIndex' | 'sessionId'>) => {
    ensureSessionStarted();
    const indexedStep: OrderStep = {
      ...step,
      sessionId: sessionIdRef.current ?? undefined,
      stepIndex: stepIndexRef.current,
    };
    stepIndexRef.current += 1;

    setOrderState(prev => ({
      ...prev,
      steps: [...prev.steps, indexedStep],
    }));
  }, [ensureSessionStarted]);

  // Speech recognition
  const { transcript, finalTranscript, resetTranscript } = useSpeechRecognition();

  // Update refs when props change
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    cartRef.current = cart;
    // Reset activity timer when cart changes (items added/removed)
    lastActivityRef.current = Date.now();
    setIsWarningPhase(false);
  }, [cart]);

  // Loading dots animation
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (orderState.isProcessingOrder || orderState.isConfirmingOrder) {
      intervalId = setInterval(() => {
        setOrderState(prev => ({
          ...prev,
          loadingDots: (prev.loadingDots + 1) % 3
        }));
      }, 300);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [orderState.isProcessingOrder, orderState.isConfirmingOrder]);

  // Text-to-speech with echo prevention (pause recognition while speaking)
  useEffect(() => {
    if (speech !== null && speech !== '') {
      try {
        // Cancel any ongoing speech to avoid overlap
        if (window.speechSynthesis.speaking) {
          window.speechSynthesis.cancel();
        }

        const msg = new SpeechSynthesisUtterance();
        msg.text = speech;
        msg.lang = 'fr-FR';

        msg.onstart = () => {
          // Mark speaking and stop listening to avoid self-capture
          ttsSpeakingRef.current = true;
          try { SpeechRecognition.stopListening(); } catch (e) { /* noop */ }
          // Clear any partial transcript that may have been captured
          try { resetTranscript(); } catch (e) { /* noop */ }
        };

        const restartListening = () => {
          // Small delay helps avoid tail-end capture of TTS
          setTimeout(() => {
            if (!isEnabled) return;
            try {
              SpeechRecognition.startListening({
                continuous: true,
                language: 'fr-FR',
                interimResults: false,
              });
            } catch (e) { /* noop */ }
          }, 200);
        };

        msg.onend = () => {
          ttsSpeakingRef.current = false;
          restartListening();
        };
        msg.onerror = () => {
          ttsSpeakingRef.current = false;
          restartListening();
        };

        window.speechSynthesis.speak(msg);
      } catch (err) {
        // Fallback: if TTS fails, ensure recognition is running
        if (!isEnabled) return;
        try {
          SpeechRecognition.startListening({
            continuous: true,
            language: 'fr-FR',
            interimResults: false,
          });
        } catch (e) { /* noop */ }
      }
    }
  }, [speech, resetTranscript, isEnabled]);

  // Initialize speech recognition
  useEffect(() => {
    if (!isEnabled) {
      // Cleanly stop recognition if disabled
      try { SpeechRecognition.stopListening(); } catch (e) { /* noop */ }
      return undefined;
    }

    if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
      console.error('Le navigateur ne supporte pas la reconnaissance vocale');
      return undefined;
    }

    console.log('Démarrage de la reconnaissance vocale avec les paramètres français...');

    SpeechRecognition.startListening({
      continuous: true,
      language: 'fr-FR',
      interimResults: false,
    });

    return () => {
      try { SpeechRecognition.stopListening(); } catch (e) { /* noop */ }
    };
  }, [isEnabled]);

  // API Functions
  const sendTranscriptToPython = useCallback(async (transcriptToSend: string) => {
    await ensureSessionStarted();
    const startedAt = performance.now();
    try {
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;
      organizationIdRef.current = subOrgId;

      console.log('🧠 Envoi vers Python:', transcriptToSend);
      const response = await axiosRaw.post(`${process.env.REACT_APP_PYTHON_APP_HOST_API_KEY}/extract_order/${subOrgId}`, {
        transcript: transcriptToSend
      });
      const latencyMs = Math.round(performance.now() - startedAt);

      const { data }: { data: { items: ExtractedItem[] } } = response;

      // Filter valid items
      const validItems = data.items.filter(item =>
        isNumeric(item.productId) && !(item.error && item.error.length > 0)
      );

      const isCorrection = nextStepIsCorrectionRef.current;
      appendStep({
        transcript: transcriptToSend,
        orderJson: JSON.stringify(data),
        stepType: isCorrection ? 'CORRECTION' : 'SIMPLE_ORDER',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: isCorrection,
        isRepeat: isCorrection,
        noResponseTimeoutHit: validItems.length === 0,
        errorCode: validItems.length === 0 ? 'NO_VALID_ITEM' : undefined,
      });
      nextStepIsCorrectionRef.current = validItems.length === 0;

      // Update solo items based on extracted items (handles additions and partial/total removals)
      setOrderState(prev => {
        let newSoloItems = [...prev.soloItems];
        let newLastId: string | null = prev.lastInteractedProductId;
        
        validItems.forEach(item => {
          const instructions = item.specialInstructions?.toLowerCase() || '';
          const globalAction = (data as any).action || 'add';

          const isRemovalIntent = globalAction === 'remove' ||
                                 instructions.includes('supprimer') ||
                                 instructions.includes('enlever') ||
                                 instructions.includes('enlève') ||
                                 instructions.includes('retirer') ||
                                 instructions.includes('supprimé') ||
                                 instructions.includes('viré') ||
                                 instructions.includes('moins') ||
                                 instructions.includes('enlève-moi') ||
                                 instructions.includes('retire-moi') ||
                                 instructions.includes('sans ça') ||
                                 instructions.includes('pas de') ||
                                 instructions.includes('j\'en veux pas') ||
                                 instructions.includes('enlève juste') ||
                                 instructions.includes('garde pas') ||
                                 instructions.includes('supprime-moi') ||
                                 instructions.includes('l\'enlever') ||
                                 instructions.includes('sans mettre') ||
                                 instructions.includes('no more') ||
                                 instructions.includes('take out') ||
                                 instructions.includes('leave out') ||
                                 instructions.includes('remove that') ||
                                 instructions.includes('without') ||
                                 instructions.includes('hold the') ||
                                 instructions.includes('skip');
          
          const isAdditionIntent = globalAction === 'add' && (
                                  instructions.includes('ajouter') ||
                                  instructions.includes('ajoute') ||
                                  instructions.includes('rajoute') ||
                                  instructions.includes('rajouter') ||
                                  instructions.includes('encore') ||
                                  instructions.includes('autre') ||
                                  instructions.includes('plus') ||
                                  instructions.includes('add') ||
                                  instructions.includes('more') ||
                                  instructions.includes('mets-moi') ||
                                  instructions.includes('je prends') ||
                                  instructions.includes('je vais prendre') ||
                                  instructions.includes('donne-moi un') ||
                                  instructions.includes('file-moi') ||
                                  instructions.includes('ajoute-moi') ||
                                  instructions.includes('j\'aimerais') ||
                                  instructions.includes('ça sera') ||
                                  instructions.includes('je veux aussi') ||
                                  instructions.includes('avec') ||
                                  instructions.includes('mets') ||
                                  instructions.includes('i want') ||
                                  instructions.includes('i\'ll take') ||
                                  instructions.includes('can i get') ||
                                  instructions.includes('give me') ||
                                  instructions.includes('put') ||
                                  instructions.includes('include') ||
                                  instructions.includes('make it')
                                 );

          const isAbsoluteIntent = globalAction === 'reset' ||
                                  instructions.includes('juste') ||
                                  instructions.includes('seulement') ||
                                  instructions.includes('uniquement') ||
                                  instructions.includes('total') ||
                                  instructions.includes('en tout') ||
                                  instructions.includes('just') ||
                                  instructions.includes('only');

          const isReplaceIntent = instructions.includes('remplace') ||
                                 instructions.includes('remplacer') ||
                                 instructions.includes('change') ||
                                 instructions.includes('substitue') ||
                                 instructions.includes('mets plutôt') ||
                                 instructions.includes('remplace par') ||
                                 instructions.includes('je préfère') ||
                                 instructions.includes('je change pour') ||
                                 instructions.includes('switch avec') ||
                                 instructions.includes('au lieu de ça mets') ||
                                 instructions.includes('plutôt') ||
                                 instructions.includes('switch') ||
                                 instructions.includes('change to') ||
                                 instructions.includes('make it with') ||
                                 instructions.includes('instead of') ||
                                 instructions.includes('swap') ||
                                 instructions.includes('replace with');
          
          const targetId = item.productId;
          const normalizeName = (name: string) => 
              name.toLowerCase()
                  .replace(/^(le|la|les|un|une|du|de|des)\s+/g, '')
                  .replace(/\s+/g, '')
                  .replace(/s$/g, '') // remove trailing s for plural
                  .trim();

          const targetNameNormalized = item.productName ? normalizeName(item.productName) : '';
          
          const existingIndex = newSoloItems.findIndex(si => {
              const siNameNormalized = normalizeName(si.productName);
              return si.productId === item.productId || 
                     (targetNameNormalized && siNameNormalized === targetNameNormalized);
          });

          // Handle Replacement logic
          if (isReplaceIntent) {
              let replacedId: string | null = null;
              
              // 1. Try to find if a product name is mentioned in the instructions to replace a specific item
              // e.g. "remplacer le breuvage par un jack classique"
              const lowerInstructions = instructions.toLowerCase();
              const itemsToReplace = newSoloItems.find(si => {
                  const siName = si.productName.toLowerCase();
                  return lowerInstructions.includes(siName) && si.productId !== item.productId;
              });

              if (itemsToReplace) {
                  replacedId = itemsToReplace.productId;
              } else {
                  // 2. Fallback to last interacted if no specific name match
                  replacedId = prev.lastInteractedProductId;
              }

              if (replacedId && replacedId !== item.productId) {
                  console.log(`🔄 Remplacement: suppression de ${replacedId} pour ajout de ${item.productId}`);
                  // Remove the old one
                  newSoloItems = newSoloItems.filter(si => si.productId !== replacedId);
                  // Add the new one (as an absolute state)
                  newSoloItems.push(item);
                  newLastId = item.productId;
                  return; // Skip standard processing for this item
              }
          }
          
          console.log(`Processing item ${item.productName} (ID: ${item.productId}) - Index in state: ${existingIndex}`);
          
          if (isRemovalIntent) {
            if (existingIndex > -1) {
              const existingItem = newSoloItems[existingIndex];
              if (item.quantity >= existingItem.quantity || !item.quantity) {
                newSoloItems = newSoloItems.filter(si => si.productId !== item.productId);
                newLastId = null;
              } else {
                newSoloItems[existingIndex] = { ...existingItem, quantity: existingItem.quantity - item.quantity };
                newLastId = item.productId;
              }
            }
          } else if (isAdditionIntent) {
            if (existingIndex > -1) {
              const existingItem = newSoloItems[existingIndex];
              newSoloItems[existingIndex] = { ...existingItem, quantity: existingItem.quantity + item.quantity };
            } else {
              newSoloItems.push(item);
            }
            newLastId = item.productId;
          } else {
            // Default or Absolute
            if (existingIndex > -1) {
              newSoloItems[existingIndex] = { ...newSoloItems[existingIndex], quantity: item.quantity };
            } else {
              newSoloItems.push(item);
            }
            newLastId = item.productId;
          }
        });
        
        return {
          ...prev,
          soloItems: newSoloItems,
          lastInteractedProductId: newLastId,
        };
      });

      // Update Redux cart (must match the logic above for consistency)
      validItems.forEach((item) => {
        const { productId, size, quantity } = item;
        const instructions = item.specialInstructions?.toLowerCase() || '';
        
        const globalAction = (data as any).action || 'add';

        const isRemoval = globalAction === 'remove' ||
                         instructions.includes('supprimer') ||
                         instructions.includes('enlever') ||
                         instructions.includes('retirer') ||
                         instructions.includes('moins') ||
                         instructions.includes('enlève-moi') ||
                         instructions.includes('retire-moi') ||
                         instructions.includes('sans ça') ||
                         instructions.includes('pas de') ||
                         instructions.includes('j\'en veux pas') ||
                         instructions.includes('enlève juste') ||
                         instructions.includes('garde pas') ||
                         instructions.includes('supprime-moi') ||
                         instructions.includes('l\'enlever') ||
                         instructions.includes('sans mettre') ||
                         instructions.includes('no more') ||
                         instructions.includes('take out') ||
                         instructions.includes('leave out') ||
                         instructions.includes('remove that') ||
                         instructions.includes('without') ||
                         instructions.includes('hold the') ||
                         instructions.includes('skip');
        
        const isAddition = globalAction === 'add' && (
                          instructions.includes('ajouter') ||
                          instructions.includes('ajoute') ||
                          instructions.includes('encore') ||
                          instructions.includes('plus') ||
                          instructions.includes('mets-moi') ||
                          instructions.includes('je prends') ||
                          instructions.includes('je vais prendre') ||
                          instructions.includes('donne-moi un') ||
                          instructions.includes('file-moi') ||
                          instructions.includes('ajoute-moi') ||
                          instructions.includes('j\'aimerais') ||
                          instructions.includes('ça sera') ||
                          instructions.includes('je veux aussi') ||
                          instructions.includes('avec') ||
                          instructions.includes('mets') ||
                          instructions.includes('i want') ||
                          instructions.includes('i\'ll take') ||
                          instructions.includes('can i get') ||
                          instructions.includes('give me') ||
                          instructions.includes('put') ||
                          instructions.includes('include') ||
                          instructions.includes('make it')
                         );

        const isAbsolute = globalAction === 'reset' ||
                          instructions.includes('juste') ||
                          instructions.includes('seulement') ||
                          instructions.includes('uniquement');

        const isReplace = instructions.includes('remplace') ||
                         instructions.includes('change') ||
                         instructions.includes('substitue') ||
                         instructions.includes('mets plutôt') ||
                         instructions.includes('remplace par') ||
                         instructions.includes('je préfère') ||
                         instructions.includes('je change pour') ||
                         instructions.includes('switch avec') ||
                         instructions.includes('au lieu de ça mets') ||
                         instructions.includes('plutôt') ||
                         instructions.includes('switch') ||
                         instructions.includes('change to') ||
                         instructions.includes('make it with') ||
                         instructions.includes('instead of') ||
                         instructions.includes('swap') ||
                         instructions.includes('replace with');

        if (isReplace) {
            let replacedId: string | null = null;

            // Priority: match by name from instructions
            const lowerInstructions = instructions.toLowerCase();
            const itemsToReplace = cartRef.current.find((c: any) => {
                const cName = c.name.toLowerCase();
                return lowerInstructions.includes(cName) && String(c.id) !== String(productId);
            });

            if (itemsToReplace) {
                replacedId = String(itemsToReplace.id);
            } else {
                // Secondary: use last interacted
                replacedId = orderState.lastInteractedProductId;
            }

            if (replacedId && String(replacedId) !== String(productId)) {
                dispatch(deleteCart(replacedId));
                // Add the new one
                const productInCatalog = productsRef.current.find((p: any) => String(p.id) === String(productId));
                if (productInCatalog) {
                    dispatch(addToCart({
                        id: productInCatalog.id,
                        name: getName(productInCatalog.translations),
                        cover: productInCatalog.cover,
                        available: productInCatalog.available,
                        price: productInCatalog.price,
                        colors: productInCatalog.colors,
                        size,
                        quantity,
                    }));
                }
                return;
            }
        }

        if (isRemoval) {
          const existingLine = cartRef.current.find((c: any) => c.id === productId);
          if (existingLine) {
            if (quantity >= existingLine.quantity || !quantity) {
              dispatch(deleteCart(productId));
            } else {
              for (let i = 0; i < quantity; i += 1) {
                dispatch(decreaseQuantity(productId));
              }
            }
          }
          return;
        }

        if (isAddition) {
          const productInCatalog = productsRef.current.find((p: any) => String(p.id) === String(productId));
          if (productInCatalog) {
            dispatch(addToCart({
              id: productInCatalog.id,
              name: getName(productInCatalog.translations),
              cover: productInCatalog.cover,
              available: productInCatalog.available,
              price: productInCatalog.price,
              colors: productInCatalog.colors,
              size,
              quantity,
            }));
          }
          return;
        }

        if (isAbsolute) {
          // For absolute, we delete first then add the exact quantity
          dispatch(deleteCart(productId));
          const productInCatalog = productsRef.current.find((p: any) => String(p.id) === String(productId));
          if (productInCatalog) {
            dispatch(addToCart({
              id: productInCatalog.id,
              name: getName(productInCatalog.translations),
              cover: productInCatalog.cover,
              available: productInCatalog.available,
              price: productInCatalog.price,
              colors: productInCatalog.colors,
              size,
              quantity,
            }));
          }
          return;
        }

        // Default: use absolute update to match UI
        dispatch(deleteCart(productId));
        const productInCatalog = productsRef.current.find((p: any) => String(p.id) === String(productId));
        if (productInCatalog) {
          dispatch(addToCart({
            id: productInCatalog.id,
            name: getName(productInCatalog.translations),
            cover: productInCatalog.cover,
            available: productInCatalog.available,
            price: productInCatalog.price,
            colors: productInCatalog.colors,
            size,
            quantity,
          }));
        }
      });

      setSpeech('Vérifiez votre commande sur l\'écran et dites \'confirmer la commande\' pour finaliser.');
    } catch (err) {
      const latencyMs = Math.round(performance.now() - startedAt);
      appendStep({
        transcript: transcriptToSend,
        orderJson: JSON.stringify({ error: 'PYTHON_EXTRACT_FAILED' }),
        stepType: 'ESCALATION',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: false,
        isRepeat: false,
        noResponseTimeoutHit: true,
        errorCode: 'PYTHON_EXTRACT_FAILED',
      });
      nextStepIsCorrectionRef.current = true;
      console.error('❌ Erreur Python extract_order:', err);
      setSpeech('Erreur, veuillez répéter.');
    } finally {
      await incrementAiRequestCount();
    }
  }, [appendStep, dispatch, ensureSessionStarted, getName, incrementAiRequestCount, orderState.lastInteractedProductId]);

  // Fetch trio options
  const fetchTrioOptions = useCallback(async (productId: string): Promise<TrioDetails | null> => {
    try {
      // Get fresh subOrgId
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;
      organizationIdRef.current = subOrgId;

      const response = await axiosRaw.get(`${process.env.REACT_APP_PYTHON_APP_HOST_API_KEY}/get_trio_options/${subOrgId}/${productId}`);

      // Normalize backend payload (snake_case -> camelCase) and ensure IDs are strings
      const raw = response.data as any;
      const normalized: TrioDetails = {
        product: {
          id: String(raw.product.id),
          name: raw.product.name,
          price: Number(raw.product.price) || 0,
          trioPrice: Number(raw.product.trioPrice ?? raw.product.trio_price ?? 0),
          calorie: Number(raw.product.calorie) || 0,
          description: raw.product.description,
        },
        option_groups: (raw.option_groups || []).map((g: any) => ({
          id: String(g.id),
          name: g.name,
          required: !!g.required,
          multiple: !!g.multiple,
          items: (g.items || []).map((i: any) => ({
            id: String(i.id),
            name: i.name,
            price: Number(i.price) || 0,
          })),
        })),
      };

      console.log('🎯 Trio options fetched (normalized):', normalized);

      return normalized;
    } catch (err) {
      console.error('❌ Erreur fetch trio options:', err);
      setSpeech('Erreur, veuillez répéter.');
      return null;
    }
  }, []);

  // Extract single trio item
  const extractSingleTrio = useCallback(async (transcriptText: string): Promise<string | null> => {
    await ensureSessionStarted();
    const startedAt = performance.now();
    try {
      // Get fresh subOrgId
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;
      organizationIdRef.current = subOrgId;

      console.log('🔍 Sending to extract_single_trio:', transcriptText);

      const response = await axiosRaw.post(`${process.env.REACT_APP_PYTHON_APP_HOST_API_KEY}/extract_single_trio/${subOrgId}`, {
        transcript: transcriptText
      });
      const latencyMs = Math.round(performance.now() - startedAt);

      const { data }: { data: ExtractSingleTrioResponse } = response;
      console.log('🎯 Single trio extracted:', data);

      appendStep({
        transcript: transcriptText,
        orderJson: JSON.stringify(data),
        stepType: data.error ? 'CORRECTION' : 'TRIO_SELECTION',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: !!data.error,
        isRepeat: !!data.error,
        noResponseTimeoutHit: !!data.error,
        errorCode: data.error ?? undefined,
      });

      if (data.error || !data.item) {
        nextStepIsCorrectionRef.current = true;
        setSpeech('Quel trio exactement ?');
        return null;
      }

      nextStepIsCorrectionRef.current = false;

      return data.item.productId;
    } catch (err) {
      const latencyMs = Math.round(performance.now() - startedAt);
      appendStep({
        transcript: transcriptText,
        orderJson: JSON.stringify({ error: 'PYTHON_SINGLE_TRIO_FAILED' }),
        stepType: 'ESCALATION',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: false,
        isRepeat: false,
        noResponseTimeoutHit: true,
        errorCode: 'PYTHON_SINGLE_TRIO_FAILED',
      });
      nextStepIsCorrectionRef.current = true;
      console.error('❌ Erreur extract single trio:', err);
      setSpeech('Quel trio exactement ?');
      return null;
    } finally {
      await incrementAiRequestCount();
    }
  }, [appendStep, ensureSessionStarted, incrementAiRequestCount]);

  // Process trio selections
  const processTrioSelections = useCallback(async (transcriptText: string, productId: string) => {
    await ensureSessionStarted();
    const startedAt = performance.now();
    try {
      // Get fresh subOrgId
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;
      organizationIdRef.current = subOrgId;

      const response = await axiosRaw.post(`${process.env.REACT_APP_PYTHON_APP_HOST_API_KEY}/extract_trio_selections/${subOrgId}/${productId}`, {
        transcript: transcriptText
      });
      const latencyMs = Math.round(performance.now() - startedAt);

      const { data }: { data: TrioSelectionsResponse } = response;
      console.log('🎯 Trio selections processed:', data);

      appendStep({
        transcript: transcriptText,
        orderJson: JSON.stringify(data),
        stepType: data.error ? 'CORRECTION' : 'TRIO_SELECTION',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: !!data.error,
        isRepeat: !!data.error,
        noResponseTimeoutHit: !!data.error,
        errorCode: data.error ?? undefined,
      });

      nextStepIsCorrectionRef.current = !!data.error;

      return data;
    } catch (err) {
      const latencyMs = Math.round(performance.now() - startedAt);
      appendStep({
        transcript: transcriptText,
        orderJson: JSON.stringify({ error: 'PYTHON_TRIO_SELECTION_FAILED' }),
        stepType: 'ESCALATION',
        speakerRole: 'CUSTOMER',
        latencyMs,
        wasCorrected: false,
        isRepeat: false,
        noResponseTimeoutHit: true,
        errorCode: 'PYTHON_TRIO_SELECTION_FAILED',
      });
      nextStepIsCorrectionRef.current = true;
      console.error('❌ Erreur process trio selections:', err);
      setSpeech('Erreur, veuillez répéter.');
      return null;
    } finally {
      await incrementAiRequestCount();
    }
  }, [appendStep, ensureSessionStarted, incrementAiRequestCount]);

  // Send order to Spring Boot
  const sendOrderToSpringBoot = useCallback(async () => {
    ensureSessionStarted();
    try {
      // Get fresh subOrgId
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;
      organizationIdRef.current = subOrgId;

      // Map solo items
      const soloItemsPayload = orderState.soloItems.map(item => ({
        productId: item.productId,
        size: String(item.size ?? ''),
        price: Number(item.price ?? 0),
        quantity: Number(item.quantity ?? 1),
        selections: item.selections,
      }));

      // Map trio items with selections
      const trioItemsPayload = orderState.trioItems.map(trio => {
        const selectionsArray = Object.entries(trio.selections || {}).flatMap(([groupId, items]) => {
          const gIdNum = Number(groupId);
          return (items || []).map(it => ({
            optionItemId: Number(it.item_id ?? 0),
            name: it.name,
            price: Number(it.price ?? 0),
            groupId: Number.isNaN(gIdNum) ? undefined : gIdNum,
            groupName: undefined,
          }));
        });
        return {
          productId: trio.productId,
          size: 'TRIO',
          price: Number(trio.totalPrice ?? trio.trioPrice ?? 0),
          quantity: Number(trio.quantity ?? 1),
          selections: selectionsArray,
        };
      });

      const confirmationStep: OrderStep = {
        transcript: 'confirm_order',
        orderJson: JSON.stringify({ status: 'CONFIRMED' }),
        sessionId: sessionIdRef.current ?? undefined,
        stepType: 'CONFIRMATION',
        speakerRole: 'CUSTOMER',
        stepIndex: stepIndexRef.current,
        latencyMs: 0,
        wasCorrected: false,
        isRepeat: false,
        noResponseTimeoutHit: false,
      };

      const springPayload = {
        buyerId: user?.id || 'ff808181977e4e6401977e51a9c70001',
        organizationId: subOrgId,
        sessionId: sessionIdRef.current,
        sessionStartedAt: sessionStartedAtRef.current,
        sessionEndedAt: new Date().toISOString(),
        isAbandoned: false,
        humanTakeover: false,
        orderSource: 'AI_VOICE',
        items: [...soloItemsPayload, ...trioItemsPayload],
        steps: [...orderState.steps, confirmationStep],
      };

      console.log('envoi du payload spring vers le backend java', springPayload);

      const response = await axios.post('/api/v1/orders', springPayload);
      const savedOrder = response.data;
      console.log('✅ Commande confirmée et sauvegardée:', savedOrder);

      setSpeech('Merci ! Votre commande est validée. Veuillez passer au guichet suivant.');

      // Reset everything
      dispatch(resetCart());
      setOrderState({
        soloItems: [],
        trioItems: [],
        phase: 'idle',
        trioSelection: null,
        accumulatedTranscript: '',
        pendingTranscript: '',
        steps: [],
        isProcessingOrder: false,
        isConfirmingOrder: false,
        loadingDots: 0,
        lastInteractedProductId: null,
      });
      closeCurrentSession();
      lastProcessedTranscript.current = '';
    } catch (err) {
      console.error('❌ Erreur Spring Boot:', err);
      setSpeech('Erreur, veuillez répéter.');
    }
  }, [closeCurrentSession, dispatch, ensureSessionStarted, orderState.soloItems, orderState.trioItems, orderState.steps, user?.id]);

  // Reset order
  const resetOrder = useCallback(async (customSpeech?: string) => {
    // Snapshot active session data before clearing refs/state
    const sessionIdSnapshot = sessionIdRef.current;
    const sessionStartedAtSnapshot = sessionStartedAtRef.current;
    const stepsSnapshot = [...orderState.steps];
    const stepIndexSnapshot = stepIndexRef.current;

    // 1. Trigger speech and UI reset immediately for responsiveness
    setSpeech(customSpeech || 'Commande annulée. Je vous écoute.');
    
    setOrderState({
      soloItems: [],
      trioItems: [],
      phase: 'idle',
      trioSelection: null,
      accumulatedTranscript: '',
      pendingTranscript: '',
      steps: [],
      isProcessingOrder: false,
      isConfirmingOrder: false,
      loadingDots: 0,
      lastInteractedProductId: null,
    });
    dispatch(resetCart());
    closeCurrentSession();
    lastProcessedTranscript.current = '';

    // 2. Perform backend logging in the background
    try {
      // If there is no active session, skip abandonment logging to avoid malformed KPI records.
      if (!sessionIdSnapshot || !sessionStartedAtSnapshot) {
        return;
      }

      // Get user context for organization info
      const userResponse = await axios.get('/api/v1/users/my-account');
      const authUser: any = userResponse.data;
      const { subOrgId } = authUser;

      // Create abandonment step
      const abandonmentStep: OrderStep = {
        transcript: 'oublier',
        orderJson: JSON.stringify({ status: 'ABANDONED' }),
        sessionId: sessionIdSnapshot,
        stepType: 'ABANDONMENT',
        speakerRole: 'CUSTOMER',
        stepIndex: stepIndexSnapshot,
        latencyMs: 0,
        wasCorrected: false,
        isRepeat: false,
        noResponseTimeoutHit: false,
      };

      // Send abandonment to Spring Boot backend
      const abandonPayload = {
        buyerId: user?.id || 'ff808181977e4e6401977e51a9c70001',
        organizationId: subOrgId,
        sessionId: sessionIdSnapshot,
        sessionStartedAt: sessionStartedAtSnapshot,
        sessionEndedAt: new Date().toISOString(),
        isAbandoned: true,
        humanTakeover: false,
        orderSource: 'AI_VOICE',
        items: [],
        steps: [...stepsSnapshot, abandonmentStep],
      };

      await axios.post('/api/v1/orders', abandonPayload);
      console.log('🔄 Session marquée comme abandonnée en arrière-plan');
    } catch (err) {
      console.error('⚠️ Erreur lors de l\'abandon en arrière-plan:', err);
    }
  }, [closeCurrentSession, dispatch, orderState.steps, user?.id]);

  // Inactivity Timeout Logic
  useEffect(() => {
    const checkInactivity = setInterval(() => {
      // Only run timeout if we are in an active ordering phase
      if (orderState.phase === 'ordering' || orderState.phase === 'trio_selection' || orderState.phase === 'trio_detected') {
        const secondsInactive = (Date.now() - lastActivityRef.current) / 1000;

        // Phase 1: Warning after 30 seconds
        if (secondsInactive >= 30 && secondsInactive < 60 && !isWarningPhase) {
          console.log('⚠️ Inactivity detected (30s). Warning user...');
          setIsWarningPhase(true);
          setSpeech('Êtes-vous toujours là ? Dites oui ou continuez votre commande, sinon elle sera annulée.');
        }

        // Phase 2: Abandonment after 45 seconds total
        if (secondsInactive >= 60) {
          console.log('💀 Critical inactivity (60s). Resetting order...');
          clearInterval(checkInactivity);
          resetOrder('Session expirée pour inactivité. Au revoir.');
        }
      } else {
        // Reset timer if we transition out of ordering phases (e.g. to idle or processing)
        lastActivityRef.current = Date.now();
        if (isWarningPhase) setIsWarningPhase(false);
      }
    }, 1000);

    return () => clearInterval(checkInactivity);
  }, [orderState.phase, isWarningPhase, resetOrder]);

  // Allow removing items from UI
  const removeSoloItem = useCallback((productId: string) => {
    try {
      // Update local enhanced state
      setOrderState(prev => ({
        ...prev,
        soloItems: prev.soloItems.filter((it) => it.productId !== productId),
      }));
      // Keep Redux cart in sync
      dispatch(deleteCart(productId));
    } catch (e) {
      // noop
    }
  }, [dispatch]);

  const removeTrioItem = useCallback((productId: string) => {
    setOrderState(prev => ({
      ...prev,
      trioItems: prev.trioItems.filter((it) => it.productId !== productId),
    }));
  }, []);

  // Handle trio detection
  const handleTrioDetection = useCallback(async (transcriptText: string) => {
    console.log('🎯 Traitement de la détection trio');

    const { beforeTrio, trioItem, afterTrio } = splitTranscriptAtTrio(transcriptText);

    // Process solo items before trio
    if (beforeTrio.trim()) {
      const fullBeforeTranscript = `${orderState.accumulatedTranscript} ${beforeTrio}`.trim();
      setOrderState(prev => ({
        ...prev,
        phase: 'trio_detected',
        isProcessingOrder: true,
        pendingTranscript: afterTrio
      }));

      await sendTranscriptToPython(fullBeforeTranscript);
    }

    // Extract trio product
    const productId = await extractSingleTrio(trioItem);
    if (!productId) {
      // Resume normal ordering if trio extraction failed
      setOrderState(prev => ({
        ...prev,
        phase: 'ordering',
        isProcessingOrder: false
      }));
      return;
    }

    // Fetch trio options
    const trioDetails = await fetchTrioOptions(productId);
    if (!trioDetails) {
      // Resume normal ordering if trio options fetch failed
      setOrderState(prev => ({
        ...prev,
        phase: 'ordering',
        isProcessingOrder: false
      }));
      return;
    }

    // Enter trio selection mode
    setOrderState(prev => ({
      ...prev,
      phase: 'trio_selection',
      isProcessingOrder: false,
      trioSelection: {
        trioDetails,
        currentGroupIndex: 0,
        selections: {},
        accumulatedTranscript: '',
        isLoading: false,
        error: null
      }
    }));

    // Guide user through first selection
    const firstGroup = trioDetails.option_groups[0];
    if (firstGroup) {
      const options = firstGroup.items.map(item => item.name).join(', ');
      setSpeech(`Trio ${trioDetails.product.name}. Quel ${firstGroup.name.toLowerCase()} : ${options} ?`);
    } else {
      setSpeech(`Trio ${trioDetails.product.name}. Dites 'valider' pour confirmer.`);
    }
  }, [orderState.accumulatedTranscript, sendTranscriptToPython, extractSingleTrio, fetchTrioOptions]);

  // Handle trio selection input
  const handleTrioSelectionInput = useCallback(async (transcriptText: string) => {
    if (!orderState.trioSelection) return;

    const { trioDetails, currentGroupIndex, selections } = orderState.trioSelection;
    const currentGroup = trioDetails.option_groups[currentGroupIndex];

    if (!currentGroup) return;

    // Toggle loading ON
    setOrderState(prev => ({
      ...prev,
      trioSelection: prev.trioSelection ? { ...prev.trioSelection, isLoading: true } : null,
    }));

    try {
      // Process selection for current group
      const selectionResult = await processTrioSelections(transcriptText, trioDetails.product.id);

      if (selectionResult && !selectionResult.error) {
        // Normalize selections keys: remap any generic key like 'group_id' to the actual current group ID
        const validGroupIds = new Set(trioDetails.option_groups.map(g => String(g.id)));
        const normalizedSelections: Record<string, any[]> = {};
        Object.entries(selectionResult.selections || {}).forEach(([groupKey, items]) => {
          const key = validGroupIds.has(groupKey) ? groupKey : String(currentGroup.id);
          normalizedSelections[key] = items as any[];
        });

        const newSelections = { ...selections, ...normalizedSelections };

        // Move to next group or complete selection
        const nextGroupIndex = currentGroupIndex + 1;

        if (nextGroupIndex < trioDetails.option_groups.length) {
          // Move to next group
          const nextGroup = trioDetails.option_groups[nextGroupIndex];
          const options = nextGroup.items.map(item => item.name).join(', ');

          setOrderState(prev => ({
            ...prev,
            trioSelection: prev.trioSelection ? {
              ...prev.trioSelection,
              currentGroupIndex: nextGroupIndex,
              selections: newSelections
            } : null
          }));

          setSpeech(`Noté. Quel ${nextGroup.name.toLowerCase()} : ${options} ?`);
        } else {
          // All groups completed
          setOrderState(prev => ({
            ...prev,
            trioSelection: prev.trioSelection ? {
              ...prev.trioSelection,
              selections: newSelections
            } : null
          }));

          setSpeech('Trio complet. Dites \'valider\' pour confirmer.');
        }
      } else {
        setSpeech(`Quel ${currentGroup.name.toLowerCase()} : ${currentGroup.items.map(item => item.name).join(', ')} ?`);
      }
    } finally {
      // Toggle loading OFF
      setOrderState(prev => ({
        ...prev,
        trioSelection: prev.trioSelection ? { ...prev.trioSelection, isLoading: false } : null,
      }));
    }
  }, [orderState.trioSelection, processTrioSelections]);

  // Handle trio validation
  const handleTrioValidation = useCallback(() => {
    if (!orderState.trioSelection) return;

    const { trioDetails, selections } = orderState.trioSelection;

    // Calculate total price
    // Use nullish coalescing to avoid falling back when trioPrice is 0
    const baseTrioPrice = (trioDetails.product.trioPrice ?? trioDetails.product.price ?? 0);
    let totalPrice = baseTrioPrice;
    Object.values(selections).forEach(groupSelections => {
      groupSelections.forEach(item => {
        totalPrice += (item.price || 0);
      });
    });

    const completedTrio: CompletedTrioItem = {
      productId: trioDetails.product.id,
      productName: trioDetails.product.name,
      trioPrice: baseTrioPrice,
      quantity: 1,
      selections,
      totalPrice
    };

    // Add to trio items and resume ordering
    setOrderState(prev => ({
      ...prev,
      phase: 'ordering',
      trioItems: [...prev.trioItems, completedTrio],
      trioSelection: null,
      soloItems: prev.soloItems.filter(item => item.productId !== trioDetails.product.id)
    }));

    setSpeech('Trio ajouté.');
  }, [orderState.trioSelection]);

  // Main transcript processing logic
  useEffect(() => {
    if (!finalTranscript) return;

    // Ignore any transcript captured while TTS is speaking
    if (ttsSpeakingRef.current) {
      resetTranscript();
      return;
    }

    // Skip if we've already processed this exact transcript
    if (finalTranscript === lastProcessedTranscript.current) return;
    lastProcessedTranscript.current = finalTranscript;

    const command = detectCommand(finalTranscript);

    console.log('🎤 Transcription finale:', finalTranscript);
    console.log('🎯 Commande détectée:', command);
    console.log('🎯 Phase actuelle:', orderState.phase);

    // Handle commands based on current phase
    if (command === 'RESET') {
      console.log('🔄 Commande de réinitialisation détectée');
      resetOrder();
      resetTranscript();
      return;
    }

    // Reset activity timer on any detected speech
    lastActivityRef.current = Date.now();
    if (isWarningPhase) setIsWarningPhase(false);

    if (command === 'STAY_ALIVE') {
      console.log('💓 Stay alive command detected. Timer reset.');
      setSpeech('D\'accord, je vous écoute.');
      resetTranscript();
      return;
    }

    if (command === 'START_ORDER' && orderState.phase === 'idle') {
      console.log('🚀 Commande de démarrage détectée');
      ensureSessionStarted();
      setOrderState(prev => ({
        ...prev,
        phase: 'ordering',
        accumulatedTranscript: '',
        pendingTranscript: ''
      }));
      setSpeech('Bonjour, je vous écoute.');
      resetTranscript();
      return;
    }

    // Voice-based removal available broadly (except during confirming/processing)
    if (command === 'REMOVE_ITEM') {
      const targetName = extractRemovalTarget(finalTranscript);
      if (!targetName) {
        setSpeech('Quel article dois-je retirer ?');
        resetTranscript();
        return;
      }
      const wantsTrio = finalTranscript.toLowerCase().includes('trio');
      const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const tn = normalize(targetName);

      // Try match trio first if explicitly said 'trio'
      const trioMatch = orderState.trioItems.find(it => normalize(it.productName).includes(tn));
      if (wantsTrio && trioMatch) {
        removeTrioItem(trioMatch.productId);
        setSpeech(`Article trio ${trioMatch.productName} retiré.`);
        resetTranscript();
        return;
      }

      // Solo match by current solo items
      const soloMatch = orderState.soloItems.find(it => normalize(it.productName).includes(tn));
      if (soloMatch) {
        removeSoloItem(soloMatch.productId);
        setSpeech(`${soloMatch.productName} retiré.`);
        resetTranscript();
        return;
      }

      // Fallback: try catalog names (using translated name)
      const prod = productsRef.current.find((p: any) => getName(p.translations).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(tn));
      if (prod) {
        removeSoloItem(String(prod.id));
        setSpeech(`${getName(prod.translations)} retiré.`);
      } else if (trioMatch) {
        // If earlier trioMatch existed without wantsTrio
        removeTrioItem(trioMatch.productId);
        setSpeech(`Article trio ${trioMatch.productName} retiré.`);
      } else {
        setSpeech('Article introuvable.');
      }
      resetTranscript();
      return;
    }

    if (orderState.phase === 'ordering') {
      // Check for trio detection
      if (containsTrioKeyword(finalTranscript)) {
        console.log('🎯 Trio détecté dans la transcription');
        handleTrioDetection(finalTranscript);
        resetTranscript();
        return;
      }

      // Check for THANK YOU (end of order input)
      if (command === 'THANK_YOU') {
        console.log('🙏 Merci détecté - envoi vers Python');
        const finalOrderTranscript = `${orderState.accumulatedTranscript} ${finalTranscript}`.trim();

        if (finalOrderTranscript) {
          setOrderState(prev => ({
            ...prev,
            phase: 'processing',
            isProcessingOrder: true,
            accumulatedTranscript: finalOrderTranscript
          }));

          setSpeech('Merci ! Veuillez vérifier votre commande sur l\'écran et dire \'confirmer la commande\' pour terminer.');

          sendTranscriptToPython(finalOrderTranscript).finally(() => {
            setOrderState(prev => ({
              ...prev,
              phase: 'idle',
              isProcessingOrder: false,
              accumulatedTranscript: '' // Clear accumulated transcript after processing
            }));
          });
        } else {
          // Nothing left to process (items likely added incrémentalement)
          setOrderState(prev => ({
            ...prev,
            phase: 'idle',
            isProcessingOrder: false,
          }));
          setSpeech('Merci ! Veuillez vérifier votre commande sur l\'écran et dire \'confirmer la commande\' pour terminer.');
        }

        resetTranscript();
        return;
      }

      // Incremental processing of solo items: send the current phrase to Python
      const incrementalText = finalTranscript.trim();
      if (incrementalText) {
        console.log('📝 Envoi incrémental vers Python pour items solo:', incrementalText);
        setOrderState(prev => ({ ...prev, isProcessingOrder: true }));
        sendTranscriptToPython(incrementalText)
          .finally(() => setOrderState(prev => ({ ...prev, isProcessingOrder: false })));
      }
      resetTranscript();
      return;
    }

    // Handle trio selection phase
    if (orderState.phase === 'trio_selection' && orderState.trioSelection) {
      const { trioDetails, currentGroupIndex } = orderState.trioSelection;
      const groups = trioDetails.option_groups;
      const currentGroup = groups[currentGroupIndex];

      if (command === 'VALIDATE_TRIO') {
        console.log('✅ Validation trio détectée');
        handleTrioValidation();
        resetTranscript();
        return;
      }

      if (command === 'NEXT_GROUP') {
        const nextIdx = currentGroupIndex + 1;
        if (nextIdx < groups.length) {
          const nextGroup = groups[nextIdx];
          const options = nextGroup.items.map(item => item.name).join(', ');
          setOrderState(prev => ({
            ...prev,
            trioSelection: prev.trioSelection ? { ...prev.trioSelection, currentGroupIndex: nextIdx } : null,
          }));
          setSpeech(`Suivant. Quel ${nextGroup.name.toLowerCase()} : ${options} ?`);
        } else {
          setSpeech('Trio complet. Dites \'valider\' pour confirmer.');
        }
        resetTranscript();
        return;
      }

      if (command === 'PREV_GROUP') {
        const prevIdx = currentGroupIndex - 1;
        if (prevIdx >= 0) {
          const prevGroup = groups[prevIdx];
          const options = prevGroup.items.map(item => item.name).join(', ');
          setOrderState(prev => ({
            ...prev,
            trioSelection: prev.trioSelection ? { ...prev.trioSelection, currentGroupIndex: prevIdx } : null,
          }));
          setSpeech(`Retour. Quel ${prevGroup.name.toLowerCase()} : ${options} ?`);
        } else {
          setSpeech('Retour. Vous êtes au début.');
        }
        resetTranscript();
        return;
      }

      if (command === 'CANCEL_TRIO') {
        // Abort trio configuration and resume ordering; keep solo items untouched
        const mergedTranscript = `${orderState.accumulatedTranscript} ${orderState.pendingTranscript}`.trim();
        setOrderState(prev => ({
          ...prev,
          phase: 'ordering',
          trioSelection: null,
          accumulatedTranscript: mergedTranscript,
          pendingTranscript: '',
        }));
        setSpeech('Trio annulé. Je vous écoute.');
        resetTranscript();
        return;
      }

      if (command === 'REPEAT_OPTIONS') {
        if (currentGroup) {
          const options = currentGroup.items.map(item => item.name).join(', ');
          setSpeech(`Je répète. Quel ${currentGroup.name.toLowerCase()} : ${options} ?`);
        }
        resetTranscript();
        return;
      }

      // Default: treat as selection for the current group
      handleTrioSelectionInput(finalTranscript);
      resetTranscript();
      return;
    }

    // Handle CONFIRMATION command
    if (command === 'CONFIRM_ORDER' && orderState.phase === 'idle' &&
      (orderState.soloItems.length > 0 || orderState.trioItems.length > 0)) {
      console.log('✅ Confirmation de commande détectée');
      setOrderState(prev => ({
        ...prev,
        phase: 'confirming',
        isConfirmingOrder: true
      }));

      sendOrderToSpringBoot().finally(() => {
        setOrderState(prev => ({
          ...prev,
          isConfirmingOrder: false
        }));
      });
      resetTranscript();
      return;
    }

    // Default case
    console.log('⏳ En attente d\'une commande valide...');
    resetTranscript();

  }, [
    finalTranscript,
    orderState.phase,
    orderState.accumulatedTranscript,
    orderState.pendingTranscript,
    orderState.soloItems.length,
    orderState.trioItems.length,
    orderState.trioSelection,
    orderState.soloItems,
    orderState.trioItems,
    resetTranscript,
    sendOrderToSpringBoot,
    ensureSessionStarted,
    resetOrder,
    handleTrioDetection,
    handleTrioSelectionInput,
    handleTrioValidation,
    sendTranscriptToPython,
    removeSoloItem,
    removeTrioItem,
    getName,
    isWarningPhase
  ]);


  // Get instructions
  const getInstructions = () => generateInstructions(
    orderState.phase,
    orderState.isProcessingOrder || orderState.isConfirmingOrder,
    orderState.loadingDots,
    orderState.trioSelection,
    orderState.soloItems.length,
    orderState.trioItems.length
  );

  return {
    // States
    isOrderActive: orderState.phase === 'ordering',
    isProcessingOrder: orderState.isProcessingOrder,
    isConfirmingOrder: orderState.isConfirmingOrder,
    accumulatedTranscript: orderState.accumulatedTranscript,
    loadingDots: orderState.loadingDots,
    aiRequestCount,

    // Enhanced states
    phase: orderState.phase,
    trioSelection: orderState.trioSelection,

    // Items
    soloItems: orderState.soloItems,
    trioItems: orderState.trioItems,
    extractedData: { items: orderState.soloItems }, // For backward compatibility

    // Transcripts
    currentTranscript: transcript,

    // Instructions
    instructions: getInstructions(),

    // Actions
    resetOrder,
    removeSoloItem,
    removeTrioItem
  };
}
